import { index, jsonb, pgTable, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { id } from './_shared';

// =============================================================================
// MARKETING CONTENT DRAFTS — WO-3 Part C1 (docs/handoff/WO-3-launch-momentum.md).
// SQL mirror: docs/design/marketing-content-drafts-neon.sql (additive, applied
// via the Neon MCP tool by the orchestrator, never by drizzle-kit migrate on
// the shared branch, per CLAUDE.md hard rule 11).
//
// This is app-internal marketing content-ops data — Jason-the-operator
// reviewing drafts about the product's own channels — NOT per-user data.
// Deliberately NO users.id foreign key / no user_id column: the multi-user
// per-UUID-segment law (CLAUDE.md mission section) governs data ABOUT a
// person, not the product's own marketing drafts about itself.
//
// NO INVENTION applies here too: sourceRefs records exactly which real,
// queryable inputs justified the draft's claims (MarketingContentDraftService
// never lets the model invent a stat or a testimonial). status only ever
// moves draft -> approved | rejected via a human action at the review route
// (marketing-drafts.routes.ts) — no code path anywhere sets 'published' or
// 'sent'; a send/publish step is explicitly out of scope for this stream and
// needs its own future JASON-GATE (WO-3 Part C, JASON-GATE #4).
// =============================================================================

export const MARKETING_CONTENT_DRAFT_KINDS = ['digest', 'social', 'blog', 'email'] as const;
export type MarketingContentDraftKind = (typeof MARKETING_CONTENT_DRAFT_KINDS)[number];

export const MARKETING_CONTENT_DRAFT_STATUSES = ['draft', 'approved', 'rejected'] as const;
export type MarketingContentDraftStatus = (typeof MARKETING_CONTENT_DRAFT_STATUSES)[number];

/** What real, queryable input justified a draft's claims — the per-draft
 *  citation list NO INVENTION requires (mirrors LakeSource's provenance
 *  shape but kept local: marketing drafts cite counts/events, not URLs). */
export interface MarketingDraftSourceRef {
  kind: string; // e.g. 'application_count', 'discovery_count', 'seed_note'
  description: string; // human-readable: what real thing this cites
  value?: string | number; // the actual queried value, when numeric
  asOf?: string; // ISO timestamp the source was read
}

export const marketingContentDrafts = pgTable(
  'marketing_content_drafts',
  {
    id: id(),
    kind: text('kind').$type<MarketingContentDraftKind>().notNull(),
    title: text('title').notNull(),
    body: text('body').notNull(),
    sourceRefs: jsonb('source_refs').$type<MarketingDraftSourceRef[]>(),
    status: text('status').$type<MarketingContentDraftStatus>().notNull().default('draft'),
    generatedBy: text('generated_by'),
    aiCallLogId: uuid('ai_call_log_id'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
    reviewedAt: timestamp('reviewed_at', { withTimezone: true }),
    reviewedBy: text('reviewed_by'),
  },
  (t) => ({
    statusIdx: index('idx_marketing_content_drafts_status').on(t.status),
    createdAtIdx: index('idx_marketing_content_drafts_created_at').on(t.createdAt),
  }),
);
